import { useEffect, useRef, useState } from 'react'
import { CustomColorsRef, IconSizes, UploadStatus } from 'types/general'

import IconRender from './IconRender'
import Loading from './loading/Loading'

type FileDropZoneProps = {
  label: string
  name: string
  accept?: string
  multiple?: boolean
  photo?: boolean
  video?: boolean
  file?: boolean
  /** When true with photo, also accept PDF (lease contracts, scanned ID). */
  allowPdf?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void
  uploadStatus?: UploadStatus
}

function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true
  const name = file.name.toLowerCase()
  return (
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.webp') ||
    name.endsWith('.gif')
  )
}

function isPdfFile(file: File): boolean {
  return (
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  )
}

function FilePreviewCard({ file }: { file: File }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const image = isImageFile(file)
  const pdf = isPdfFile(file)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex max-h-52 items-center justify-center bg-gray-100">
        {previewUrl && image ? (
          <img
            src={previewUrl}
            alt={file.name}
            className="max-h-52 w-full object-contain"
          />
        ) : null}
        {previewUrl && pdf ? (
          <iframe
            src={previewUrl}
            title={file.name}
            className="h-52 w-full border-0 bg-white"
          />
        ) : null}
        {previewUrl && !image && !pdf ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-gray-500">
            <IconRender
              icon="regular-file"
              size={IconSizes.LG}
              color={CustomColorsRef.Secondary}
            />
            <span className="text-xs">Preview unavailable</span>
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-2">
        <span className="min-w-0 truncate text-xs font-medium text-gray-700">
          {file.name}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11px] text-gray-400">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </span>
          {previewUrl ? (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-[11px] font-semibold text-imovPrimary hover:underline"
            >
              Open
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const FileDropZone = ({
  label,
  name,
  accept,
  multiple = true,
  photo = true,
  video = false,
  file = false,
  allowPdf = false,
  onChange,
  onDrop,
  uploadStatus,
}: FileDropZoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const getAcceptAttribute = () => {
    if (accept) return accept

    const acceptedTypes: string[] = []
    if (photo) acceptedTypes.push('image/jpeg', 'image/jpg', 'image/png', 'image/webp')
    if (allowPdf) acceptedTypes.push('application/pdf', '.pdf')
    if (video) acceptedTypes.push('video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov')
    if (file) acceptedTypes.push('.pdf', '.doc', '.docx', '.txt', '.csv', '.xlsx')

    return acceptedTypes.join(',')
  }

  const validateFileType = (candidate: File): boolean => {
    if (!photo && !video && !file && !allowPdf) return true

    const fileType = candidate.type
    const fileName = candidate.name.toLowerCase()

    if (allowPdf) {
      if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) return true
    }

    if (photo) {
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      if (imageTypes.includes(fileType)) return true
      if (
        fileName.endsWith('.jpg') ||
        fileName.endsWith('.jpeg') ||
        fileName.endsWith('.png') ||
        fileName.endsWith('.webp')
      ) {
        return true
      }
    }

    if (video) {
      const videoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov']
      if (videoTypes.includes(fileType)) return true
      if (
        fileName.endsWith('.mp4') ||
        fileName.endsWith('.webm') ||
        fileName.endsWith('.ogg') ||
        fileName.endsWith('.avi') ||
        fileName.endsWith('.mov')
      ) {
        return true
      }
    }

    if (file) {
      const fileExtensions = ['.pdf', '.doc', '.docx', '.txt', '.csv', '.xlsx']
      return fileExtensions.some(ext => fileName.endsWith(ext))
    }

    return false
  }

  const mergeFiles = (incoming: File[]): File[] => {
    if (!multiple) return incoming.slice(0, 1)
    return [...selectedFiles, ...incoming]
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const validFiles = Array.from(files).filter(validateFileType)
      if (validFiles.length !== files.length) {
        alert('Some files were rejected due to invalid file type')
      }

      if (validFiles.length > 0) {
        const updatedFiles = mergeFiles(validFiles)
        setSelectedFiles(updatedFiles)

        const newEvent = {
          ...e,
          target: {
            ...e.target,
            files: updatedFiles as unknown as FileList,
          },
        }
        onChange?.(newEvent as React.ChangeEvent<HTMLInputElement>)
      }
    } else {
      onChange?.(e)
    }
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    const validFiles = files.filter(validateFileType)

    if (validFiles.length !== files.length) {
      alert('Some files were rejected due to invalid file type')
    }

    if (validFiles.length > 0) {
      const updatedFiles = mergeFiles(validFiles)
      setSelectedFiles(updatedFiles)

      const newEvent = {
        ...e,
        dataTransfer: {
          ...e.dataTransfer,
          files: updatedFiles as unknown as FileList,
        },
      }

      if (onDrop) {
        onDrop(newEvent as React.DragEvent<HTMLDivElement>)
      }
    }
  }

  const getDropZoneText = () => {
    const types = []
    if (photo) types.push('Images')
    if (allowPdf) types.push('PDF')
    if (video) types.push('Videos')
    if (file) types.push('Files')

    if (types.length === 0) return 'Click or drag files here'
    if (types.length === 1) return `Click or drag ${types[0]} here`
    if (types.length === 2) return `Click or drag ${types[0]} and ${types[1]} here`
    return `Click or drag ${types.slice(0, -1).join(', ')} and ${types[types.length - 1]} here`
  }

  const getAcceptedTypesText = () => {
    const types = []
    if (photo) types.push(allowPdf ? 'JPG, PNG, WEBP, PDF' : 'JPG, PNG, WEBP')
    else if (allowPdf) types.push('PDF')
    if (video) types.push('MP4, WEBM, AVI, MOV')
    if (file) types.push('PDF, DOC, TXT, CSV')

    return types.join(', ')
  }

  const clearFiles = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedFiles([])
    if (inputRef.current) {
      inputRef.current.value = ''
    }
    const emptyEvent = {
      target: { files: null },
    } as React.ChangeEvent<HTMLInputElement>
    onChange?.(emptyEvent)
  }

  return (
    <div className="mb-4">
      <label
        htmlFor={name}
        className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700"
      >
        <IconRender
          icon="regular-file-pdf"
          size={IconSizes.SM}
          className="h-4 w-4"
        />
        {label}
      </label>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClick}
        className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 hover:bg-gray-50"
      >
        <div className="flex items-center justify-center">
          {uploadStatus === UploadStatus.Uploading && <Loading />}
          {uploadStatus === UploadStatus.Error && (
            <IconRender
              icon="regular-cloud-slash"
              size={IconSizes.LG}
              color={CustomColorsRef.Danger}
            />
          )}
          {uploadStatus === UploadStatus.Success && (
            <IconRender
              icon="regular-cloud"
              size={IconSizes.LG}
              color={CustomColorsRef.Success}
            />
          )}
          {uploadStatus !== UploadStatus.Error &&
            uploadStatus !== UploadStatus.Success &&
            uploadStatus !== UploadStatus.Uploading && (
              <>
                <IconRender
                  icon="regular-cloud-arrow-up"
                  size={IconSizes.LG}
                  className="mr-2"
                />
                {getDropZoneText()}
              </>
            )}
        </div>
        {uploadStatus !== UploadStatus.Error &&
          uploadStatus !== UploadStatus.Success && (
            <p className="mt-1 text-xs">Max 5MB. Accepted: {getAcceptedTypesText()}</p>
          )}
      </div>

      {selectedFiles.length > 0 && (
        <div
          className="mt-3 rounded-lg bg-gray-50 p-3"
          onClick={e => e.stopPropagation()}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Selected Files ({selectedFiles.length})
            </span>
            <button
              type="button"
              onClick={clearFiles}
              className="text-xs text-red-600 underline hover:text-red-800"
            >
              Clear All
            </button>
          </div>
          <div className="max-h-72 space-y-3 overflow-y-auto">
            {selectedFiles.map((selected, index) => (
              <FilePreviewCard
                key={`${selected.name}-${selected.size}-${selected.lastModified}-${index}`}
                file={selected}
              />
            ))}
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        id={name}
        name={name}
        className="hidden"
        accept={getAcceptAttribute()}
        multiple={multiple}
        onChange={handleFileChange}
      />
    </div>
  )
}

export default FileDropZone
